import type {
  BkashPaymentInitiateDto,
  BkashSubmitDto,
  PaginatedResponseDto,
  PaymentRecordDto,
} from '../../domain/contracts/api.contracts';
import { PaymentChannel } from '../../domain/enums/SystemEnums';
import { apiClient } from './apiClient';
import {
  moneyToWire,
  paymentChannelToWire,
  paymentFromWire,
  paymentPageFromWire,
  paymentStatusToWire,
} from './wireAdapters';

/** E18 query params (orchestrator §6). */
export interface PaymentHistoryParams {
  page?: number;
  limit?: number;
  channel?: PaymentChannel;
  status?: 'PENDING' | 'VERIFIED' | 'REJECTED';
  dateFrom?: string; // YYYY-MM-DD
  dateTo?: string; // YYYY-MM-DD
}

/** E18 — GET /payments/history (paged) */
export async function getPaymentHistory(
  params: PaymentHistoryParams,
): Promise<PaginatedResponseDto<PaymentRecordDto>> {
  const wire = { ...params };
  if (wire.channel) wire.channel = paymentChannelToWire(wire.channel) as PaymentChannel;
  if (wire.status) wire.status = paymentStatusToWire(wire.status) as PaymentHistoryParams['status'];
  const { data } = await apiClient.get<PaginatedResponseDto<Record<string, unknown>>>(
    '/payments/history',
    { params: wire },
  );
  return paymentPageFromWire(data);
}

/** @deprecated E11 redirect checkout is not built; use `submitBkashPayment`. Kept for reference. */
export async function initiateBkashPayment(
  req: BkashPaymentInitiateDto,
): Promise<{ paymentID: string; redirectURL: string; amount: number; paymentType: string }> {
  const { data } = await apiClient.post('/payments/bkash/create', req);
  return data;
}

/** @deprecated E12 is not built; use `submitBkashPayment`. Kept for reference. */
export async function executeBkashPayment(
  paymentID: string,
): Promise<PaymentRecordDto> {
  const { data } = await apiClient.post<PaymentRecordDto>('/payments/bkash/execute', { paymentID });
  return data;
}

/** POST /payments/bkash/submit — manual bKash payment, created PENDING until an admin verifies the TrxID. */
export async function submitBkashPayment(req: BkashSubmitDto): Promise<PaymentRecordDto> {
  const { data } = await apiClient.post<Record<string, unknown>>('/payments/bkash/submit', {
    ...req,
    amount: moneyToWire(req.amount),
  });
  return paymentFromWire(data);
}

/** Fields accompanying the E13 multipart upload (BankSlipUploadRequestDto meta). */
export interface BankSlipUploadFields {
  subscriptionId: string;
  bankName: string;
  branchName: string;
  /** Bank scroll / deposit-slip reference — required by the backend (E13). */
  scrollId: string;
  depositDate: string; // YYYY-MM-DD
  amount: number;
}

/**
 * E13 — POST /payments/bank-slip/upload (multipart/form-data, ≤5 MB, image/*).
 * The apiClient request interceptor drops the JSON content type for FormData so the
 * browser sets the multipart boundary itself.
 */
export async function uploadBankSlip(
  fields: BankSlipUploadFields,
  file: File,
): Promise<PaymentRecordDto> {
  const form = new FormData();
  form.append('subscriptionId', fields.subscriptionId);
  form.append('bankName', fields.bankName);
  form.append('branchName', fields.branchName);
  form.append('scrollId', fields.scrollId);
  form.append('depositDate', fields.depositDate);
  form.append('amount', moneyToWire(fields.amount));
  form.append('slipImage', file);
  const { data } = await apiClient.post<Record<string, unknown>>('/payments/bank-slip/upload', form);
  return paymentFromWire(data);
}
